import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Clock, 
  ArrowRight, 
  ArrowLeft, 
  CheckCircle, 
  XCircle,
  Trophy,
  Star,
  Target
} from 'lucide-react';
import axios from 'axios';

interface Question {
  id: number;
  question_text: string;
  question_type: 'true_false' | 'single_choice' | 'multiple_choice';
  options: string[];
  points: number;
  order_index: number;
}

interface ExamData {
  id: number;
  title: string;
  duration_minutes: number;
  total_questions: number;
  questions: Question[];
}

const ExamInterface: React.FC = () => {
  const { examId } = useParams<{ examId: string }>();
  const navigate = useNavigate();
  const [exam, setExam] = useState<ExamData | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<{ [key: number]: any }>({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    startExam();
  }, [examId]);

  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && exam) {
      handleSubmitExam();
    }
  }, [timeLeft, exam]);

  const startExam = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`/test/${examId}/start`);
      setExam(response.data.exam);
      setTimeLeft(response.data.exam.duration_minutes * 60);
    } catch (error: any) {
      console.error('Error starting exam:', error);
      setError('Error iniciando el examen');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId: number, answer: any) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const submitAnswer = async (questionId: number, answer: any) => {
    try {
      await axios.post(`/test/${examId}/answer`, {
        questionId,
        answer
      });
    } catch (error) {
      console.error('Error submitting answer:', error);
    }
  };

  const handleNextQuestion = () => {
    const currentQuestion = exam?.questions[currentQuestionIndex];
    if (currentQuestion && answers[currentQuestion.id]) {
      submitAnswer(currentQuestion.id, answers[currentQuestion.id]);
    }
    
    if (currentQuestionIndex < (exam?.questions.length || 0) - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleSubmitExam = async () => {
    try {
      setIsSubmitting(true);
      
      // Submit all remaining answers
      for (const [questionId, answer] of Object.entries(answers)) {
        if (answer !== undefined) {
          await axios.post(`/test/${examId}/answer`, {
            questionId: parseInt(questionId),
            answer
          });
        }
      }

      // Finish exam
      const response = await axios.post(`/test/${examId}/finish`);
      
      // Navigate to results
      navigate('/dashboard', { 
        state: { 
          examResults: response.data.results,
          examTitle: exam?.title 
        } 
      });
    } catch (error: any) {
      console.error('Error submitting exam:', error);
      setError('Error enviando el examen');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimeColor = () => {
    if (timeLeft < 300) return '#ff416c'; // Red when less than 5 minutes
    if (timeLeft < 600) return '#ffc107'; // Yellow when less than 10 minutes
    return '#4ecdc4'; // Green otherwise
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p style={{ marginTop: '20px', color: 'white', fontSize: '18px' }}>
          Preparando tu examen... 🎯
        </p>
      </div>
    );
  }

  if (error || !exam) {
    return (
      <div className="container">
        <div className="card" style={{ textAlign: 'center', maxWidth: '500px', margin: '50px auto' }}>
          <h2>😔 Error</h2>
          <p>{error || 'Examen no encontrado'}</p>
          <button onClick={() => navigate('/dashboard')} className="btn btn-secondary">
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  const currentQuestion = exam.questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / exam.questions.length) * 100;

  return (
    <div className="container">
      {/* Header con timer */}
      <motion.div
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="card"
        style={{ marginBottom: '20px' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
          <div>
            <h2 style={{ color: '#333', margin: 0 }}>{exam.title}</h2>
            <p style={{ color: '#666', margin: '5px 0 0 0' }}>
              Pregunta {currentQuestionIndex + 1} de {exam.questions.length}
            </p>
          </div>
          
          <div className="timer" style={{ background: `linear-gradient(45deg, ${getTimeColor()}, #4ecdc4)` }}>
            <Clock style={{ marginRight: '8px' }} />
            {formatTime(timeLeft)}
          </div>
        </div>

        {/* Progress bar */}
        <div style={{ 
          width: '100%', 
          height: '8px', 
          background: 'rgba(0,0,0,0.1)', 
          borderRadius: '4px', 
          marginTop: '15px',
          overflow: 'hidden'
        }}>
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5 }}
            style={{
              height: '100%',
              background: 'linear-gradient(45deg, #ff6b6b, #4ecdc4)',
              borderRadius: '4px'
            }}
          />
        </div>
      </motion.div>

      {/* Pregunta actual */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentQuestionIndex}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
          transition={{ duration: 0.3 }}
          className="card"
        >
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ color: '#333', marginBottom: '20px', fontSize: '20px' }}>
              {currentQuestion.question_text}
            </h3>

            {/* Tipo de pregunta */}
            <div style={{ 
              display: 'inline-block', 
              background: '#4ecdc4', 
              color: 'white', 
              padding: '5px 15px', 
              borderRadius: '15px', 
              fontSize: '12px',
              fontWeight: 'bold',
              marginBottom: '20px'
            }}>
              {currentQuestion.question_type === 'true_false' ? 'Verdadero/Falso' :
             currentQuestion.question_type === 'single_choice' ? 'Opción Única' :
             'Opción Múltiple'}
            </div>
          </div>

          {/* Opciones de respuesta */}
          <div style={{ marginBottom: '30px' }}>
            {currentQuestion.question_type === 'true_false' ? (
              <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
                <button
                  className={`option ${answers[currentQuestion.id] === true ? 'selected' : ''}`}
                  onClick={() => handleAnswerChange(currentQuestion.id, true)}
                  style={{ flex: 1, maxWidth: '200px' }}
                >
                  <CheckCircle style={{ marginRight: '8px' }} />
                  Verdadero
                </button>
                <button
                  className={`option ${answers[currentQuestion.id] === false ? 'selected' : ''}`}
                  onClick={() => handleAnswerChange(currentQuestion.id, false)}
                  style={{ flex: 1, maxWidth: '200px' }}
                >
                  <XCircle style={{ marginRight: '8px' }} />
                  Falso
                </button>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '10px' }}>
                {currentQuestion.options.map((option, index) => (
                  <button
                    key={index}
                    className={`option ${
                      currentQuestion.question_type === 'single_choice' 
                        ? answers[currentQuestion.id] === option ? 'selected' : ''
                        : answers[currentQuestion.id]?.includes(option) ? 'selected' : ''
                    }`}
                    onClick={() => {
                      if (currentQuestion.question_type === 'single_choice') {
                        handleAnswerChange(currentQuestion.id, option);
                      } else {
                        const currentAnswers = answers[currentQuestion.id] || [];
                        const newAnswers = currentAnswers.includes(option)
                          ? currentAnswers.filter((a: string) => a !== option)
                          : [...currentAnswers, option];
                        handleAnswerChange(currentQuestion.id, newAnswers);
                      }
                    }}
                  >
                    <span style={{ marginRight: '10px', fontWeight: 'bold' }}>
                      {String.fromCharCode(65 + index)}.
                    </span>
                    {option}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Navegación */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '15px'
          }}>
            <button
              onClick={handlePreviousQuestion}
              disabled={currentQuestionIndex === 0}
              className="btn btn-secondary"
              style={{ 
                opacity: currentQuestionIndex === 0 ? 0.5 : 1,
                cursor: currentQuestionIndex === 0 ? 'not-allowed' : 'pointer'
              }}
            >
              <ArrowLeft style={{ marginRight: '8px' }} />
              Anterior
            </button>

            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <span style={{ color: '#666', fontSize: '14px' }}>
                {answers[currentQuestion.id] ? '✅ Respondida' : '❓ Sin responder'}
              </span>
            </div>

            {currentQuestionIndex === exam.questions.length - 1 ? (
              <button
                onClick={handleSubmitExam}
                disabled={isSubmitting}
                className="btn btn-success"
              >
                {isSubmitting ? (
                  <>
                    <div className="spinner" style={{ 
                      width: '20px', 
                      height: '20px', 
                      marginRight: '8px',
                      display: 'inline-block'
                    }}></div>
                    Enviando...
                  </>
                ) : (
                  <>
                    <Trophy style={{ marginRight: '8px' }} />
                    Finalizar
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={handleNextQuestion}
                className="btn"
              >
                Siguiente
                <ArrowRight style={{ marginLeft: '8px' }} />
              </button>
            )}
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Navegación rápida */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="card"
      >
        <h4 style={{ color: '#333', marginBottom: '15px', textAlign: 'center' }}>
          📋 Navegación Rápida
        </h4>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(40px, 1fr))', 
          gap: '10px' 
        }}>
          {exam.questions.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentQuestionIndex(index)}
              className={`btn ${index === currentQuestionIndex ? 'btn-success' : 'btn-secondary'}`}
              style={{ 
                minWidth: '40px', 
                height: '40px', 
                padding: '5px',
                fontSize: '14px',
                borderRadius: '50%'
              }}
            >
              {index + 1}
            </button>
          ))}
        </div>
      </motion.div>

      {/* Elementos decorativos */}
      <motion.div
        animate={{ 
          y: [0, -15, 0],
          rotate: [0, 5, 0]
        }}
        transition={{ 
          duration: 3,
          repeat: Infinity,
          ease: "easeInOut"
        }}
        style={{
          position: 'absolute',
          top: '10%',
          right: '5%',
          fontSize: '2rem',
          opacity: 0.6
        }}
      >
        🎯
      </motion.div>

      <motion.div
        animate={{ 
          y: [0, 20, 0],
          rotate: [0, -5, 0]
        }}
        transition={{ 
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut"
        }}
        style={{
          position: 'absolute',
          bottom: '10%',
          left: '5%',
          fontSize: '2rem',
          opacity: 0.7
        }}
      >
        📚
      </motion.div>
    </div>
  );
};

export default ExamInterface;
